import { Component, Ref, Vue, Prop } from 'vue-property-decorator'
import layout from '@/diagrams/configs/bubble-chart-layout'
import DiagramService from '@/app/services/diagram-data-service'
import saveAs from 'file-saver'
import minBy from 'lodash/minBy'
import maxBy from 'lodash/maxBy'
import orderBy from 'lodash/orderBy'
import palette from 'google-palette'
import projectsService from '@/projects/services/projects-service'
import Project from '@/projects/entities/project'
import { diagramViews, DiagramType, DiagramView } from '@/diagrams/models/diagram-views'
import EditNodeEvent from '@/diagrams/events/edit-node-event'
import Node from '@/diagrams/models/node'
import BubbleDiagramConfiguration from './bubble-diagram-configuration'
import DiagramConfiguration from '@/projects/entities/diagram-configuration'
import CreateNodeEvent from '@/diagrams/events/create-node-event'
import Visual from '@/diagrams/models/visual'

@Component
export default class Bubble extends Vue implements Visual {

    private chartData = new Array<any>();

    private bubble = {
        mode: 'markers',
        type: 'scatter',

        x: [] = new Array<number>(),
        y: [] = new Array<number>(),
        id: '',
        name: '',
        text: [] = new Array<string>(),

        marker: {
            color: [''],
            line: {
                color: [''],
                width: 0,
            },
            opacity: [1, 1],
            size: [] = new Array<number>(),
            symbol: "circle",
            sizeref: 1,
            sizemin: 10,
            sizemode: "diameter",
        },
    }

    private data = [this.bubble]

    private layout = layout

    @Ref()
    private diagramConfigurationDialog!: BubbleDiagramConfiguration

    private action: string = diagramViews.items.find((i) => i.type === DiagramType.Bubble)!.url
    private project: Project = new Project()
    private diagramConfiguration: DiagramConfiguration = new DiagramConfiguration()
    private fab = false

    public async setActionAsync(ctx: DiagramView) {
      this.action = ctx.url
      await this.fetchDataAsync()
      this.updateDiagram()
    }

    public async saveAsync(format: string) {
        if (format === 'svg') {
            const b64 = await (this.$refs['plotly'] as any).toImage({ format: 'svg' })
            saveAs(b64, 'bubble.svg')
        } else if (format === 'png') {
            const b64 = await (this.$refs['plotly'] as any).toImage()
            saveAs(b64, 'bubble.png')
        }
    }

    private clickPlotly(data: any) {
        if (data.points.length > 0) {
            if (data.points[0].data.id) {
                const stakeholderId = data.points[0].data.id
                const evt = new EditNodeEvent(this.project, new Node('node', 'ProjectStakeholderGroup', stakeholderId))
                this.$bus.$emit('node:edit', evt)
            }
        }
    }

    private legendClickPlotly(data: any) {
        if (data.node) {
            const stakeholderId = this.data[data.curveNumber].id
            const evt = new EditNodeEvent(this.project, new Node('node', 'ProjectStakeholderGroup', stakeholderId))
            this.$bus.$emit('node:edit', evt)
        }
    }

    private async addStakeholderGroup() {
        const parent = new Node()
        parent.dbId = this.$route.params.projectId
        const evt = new CreateNodeEvent(this.project, parent, 'ProjectStakeholderGroup')
        this.$bus.$emit('node:create', evt)
    }

    private async fetchDataAsync() {
        const id = this.$route.params.projectId
        const response = await DiagramService.getStakeHolderGroups(id)
        this.project = await projectsService.findAsync(id) || new Project()
        this.diagramConfiguration = await projectsService.findDiagramConfiguration(id) || new DiagramConfiguration()
        this.chartData = response;

        if (response.length < 1) { return }

        const min = minBy(this.chartData, (s) => s.relativeSize).relativeSize
        const max = maxBy(this.chartData, (s) => s.relativeSize).relativeSize
        const range = Math.max(1, max - min)
        const colors = [
            'd97b7acc',
            'db9168cc',
            'd3afa5cc',
            'f9c19ecc',
            'fedeabcc',
            'fff3a6cc',
            'acb9a4cc',
            '7ac6bbcc',
            '5c9fbacc',
            '5c76a0cc',
            'bdabc1cc',
            'f7bbb4cc',
            'd3e9becc',
            'f1eac0cc',
            'b6a9a1cc',
            'cdd7d9cc']
            .concat(palette('cb-Spectral', Math.max(this.chartData.length - 16, 1)))
        this.data = []

        const clonesToMove = []
        this.chartData = orderBy(this.chartData, [(d) => d.relativeSize], 'desc')
        for (let i = 0; i < this.chartData.length; i++) {
            const clone = JSON.parse(JSON.stringify(this.bubble))
            clone.id = this.chartData[i].id
            clone.x.push(this.chartData[i].interest)
            clone.y.push(this.chartData[i].influence)
            clone.name = this.chartData[i].name + '  '
            clone.text.push(this.chartData[i].name)
            clone.marker.size = [30 + ((this.chartData[i].relativeSize + min) / range * 50)]
            clone.marker.color = [colors[i]]
            if (this.data.find((c: any) => c.x[0] == clone.x[0] && c.y[0] == clone.y[0] && c.marker.size[0] == clone.marker.size[0])) {
                clonesToMove.push(clone)
            }
            this.data.push(clone)
        }

        // if clones overlap exactly, move all except the first one randomly by 0.1
        clonesToMove.forEach((c: any) => { c.x[0] += (Math.random() < 0.5 ? 0.1 : -0.1); c.y[0] += (Math.random() < 0.5 ? 0.1 : -0.1) })

        let a = layout.annotations.find((a) => a.name === 'left-bottom')
        if (a) { a.text = this.diagramConfiguration.bubbleConfig.bottomLeftQuadrantName }
        a = layout.annotations.find((a) => a.name === 'right-bottom')
        if (a) { a.text = this.diagramConfiguration.bubbleConfig.bottomRightQuadrantName }
        a = layout.annotations.find((a) => a.name === 'right-top')
        if (a) { a.text = this.diagramConfiguration.bubbleConfig.topRightQuadrantName }
        a = layout.annotations.find((a) => a.name === 'left-top')
        if (a) { a.text = this.diagramConfiguration.bubbleConfig.topLeftQuadrantName }

        this.layout = layout
    }

    private async updateDiagramConfiguration() {
        await projectsService.updateDiagramConfigurationAsync(this.project.id, this.diagramConfigurationDialog.configuration)
        this.diagramConfigurationDialog.close()
        await this.fetchDataAsync()
        this.updateDiagram()
    }

    private async openDiagramConfigurationDialog() {
        const config = await projectsService.findDiagramConfiguration(this.project.id)
        if (config) {
            this.diagramConfigurationDialog.edit(config)
        }
    }

    private updateDiagram() {
        if (this.$refs['plotly']) {
            (this.$refs['plotly'] as any).update()
        }
    }

    private async created() {
      this.$bus.$on('reload-spider', async () => {
        await this.fetchDataAsync()
        this.updateDiagram()
      })
    }

    private async mounted() {
        await this.fetchDataAsync()
        this.updateDiagram()
    }
}
